import { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";
import { useRouter } from "next/router";
import { CheckoutMode, CheckoutSessionBody } from "../utils/stripe";
import {
  getFreeTrialSubscriptionSession,
  getStripe,
  getSubscriptionCheckoutSession,
} from "../lib/stripeRequest";
import { UserExtended } from "../context/authContext/reducer";
import { useToast } from "../context/toastContext";
import { userInitialState } from "../utils/user";
import { createUserFirstInfo } from "../context/authContext/actions";

export const useSubscriptionActions = () => {
  const [user, setUser] = useState<UserExtended>(userInitialState);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsIsDisabled] = useState(false);
  const [hasCoupon, setHasCoupon] = useState(false);

  const router = useRouter();
  const toast = useToast(2500);

  const handleCheckoutClick = useCallback(async () => {
    const discounts = router.query["discounts"] as string
    const body: CheckoutSessionBody = {
      email: user.email.toLowerCase(),
      mode: CheckoutMode.subscription,
      price_id: router.query["price_id"] as string,
      discounts: discounts ? JSON.parse(discounts) : null,
    };
    const { data } = await getSubscriptionCheckoutSession(body);
    const stripe = await getStripe();
    await stripe.redirectToCheckout({ sessionId: data.id });
  }, [router.query, user.email]);

  const handleFreeTrialCheckout = useCallback(async () => {
    const body: CheckoutSessionBody = {
      email: user.email.toLowerCase(),
      mode: CheckoutMode.subscription,
      price_id: router.query["price_id"] as string,
      coupon_code: router.query["coupon_code"] as string,
    };
    const { data } = await getFreeTrialSubscriptionSession(body);
    const stripe = await getStripe();
    await stripe.redirectToCheckout({ sessionId: data.id });
  }, [router.query, user.email]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUser((user) => ({ ...user, [event.target.name]: event.target.value }));
    },
    []
  );

  const handleOnDropSelect = useCallback(
    (event: { name: string; value: string }) => {
      setUser((user) => ({ ...user, [event.name]: event.value }));
    },
    []
  );

  const checkPassword = useCallback(() => {
    if (user?.password !== user?.password_confirmation) {
      toast("error", "Passwords does not match");
      return false;
    }
    if (user?.password.length < 8 || user?.password.length > 128) {
      toast("error", "Password must be between 8 and 126 char");
      return false;
    }
    return true;
  }, [toast, user]);

  const validateForm = useCallback(() => {
    const arePasswordsValid = checkPassword();
    if (arePasswordsValid) {
      for (const [key, value] of Object.entries(user)) {
        if (
          (key === "email" ||
            key === "first_name" ||
            key === "last_name" ||
            key === "last_name" ||
            key === "password" ||
            key === "password_confirmation" ||
            key === "user_type") &&
          value === ""
        ) {
          toast("error", "Please make sure every field is filled");
          return false;
        }
      }
      return true;
    }
    return false;
  }, [checkPassword, toast, user]);

  const handleBasicSubscription = useCallback(async () => {
    const isValidForm = validateForm();
    if (isValidForm) {
      try {
        setIsLoading(true);
        setIsIsDisabled(true);
        const couponCode = router.query["coupon_code"] as string;
        await createUserFirstInfo(user, couponCode);
        await (couponCode ? handleFreeTrialCheckout : handleCheckoutClick)();
      } catch (err: AxiosError | any) {
        if (err?.data?.errors?.email)
          toast("error", `Oops, email ${err?.data?.errors?.email}`);
        else toast("error", `Oops, ${err?.data?.error}`);
      } finally {
        setIsLoading(false);
        setIsIsDisabled(false);
      }
    }
  }, [handleCheckoutClick, handleFreeTrialCheckout, router.query, toast, user, validateForm]);

  const submitUserInfo = useCallback(async () => {
    handleBasicSubscription();
  }, [handleBasicSubscription]);

  const handleEnter = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key == "Enter") {
        submitUserInfo();
      }
    },
    [submitUserInfo]
  );

  const handleRegularUpdateChange = useCallback(
    (e: { status: boolean }) => {
      setUser({
        ...user,
        regular_updates: e.status,
      });
    },
    [user]
  );

  const handleShareInformationChange = useCallback(
    (e: { status: boolean }) => {
      setUser({
        ...user,
        share_information: e.status,
      });
    },
    [user]
  );

  useEffect(() => {
    if (router.query["cancelled"]) {
      const cancelSignUp = async () => {
        const discount = router.query["discounts"] as string;
        const priceId = router.query["price_id"] as string;
        const discountParam = discount ? `&discounts=${discount}` : "";
        const priceIdParam = priceId ? `&price_id=${priceId}` : "";

        let baseReplaceUrl = `/users/subscription?${priceIdParam}${discountParam}`;
        baseReplaceUrl = hasCoupon
          ? `${baseReplaceUrl}&coupon_code=${router.query["coupon_code"]}`
          : baseReplaceUrl;
        router.replace(baseReplaceUrl, undefined, { shallow: true });

      };
      cancelSignUp();
    }

    if (router.query["coupon_code"]) {
      if (!hasCoupon) setHasCoupon(true);
    }
  }, [router, hasCoupon]);

  return {
    user,
    submitUserInfo,
    handleEnter,
    handleOnChange,
    handleOnDropSelect,
    isDisabled,
    isLoading,
    hasCoupon,
    handleRegularUpdateChange,
    handleShareInformationChange,
  };
};
